"use client"

import LegalHeroSection from "./components/LegalHeroSection";
import LegalText from "./components/LegalText";
import { Steps } from "./components/Steps";
import { LegalItemsList } from "./components/LegalItemsList";
import ContactInfo from "./components/ContactInfo";


export default function SubscriptionTermsLanding() {
  return (
    <div className="min-h-screen w-full flex flex-col items-center relative overflow-hidden">
      <div className="px-5 xl:px-[270px] mx-auto w-ful xl:py-12 py-16">
        <LegalHeroSection
          pageName="Subscription Terms"
          title="Subscription Terms for File Business INC."
          dateDescription="Effective Date: October 4, 2024- Last Updated: March 12, 2025"
          warningText="PLEASE READ THESE SUBSCRIPTION TERMS CAREFULLY BEFORE USING OUR SERVICES."
          description={`These Subscription Terms ("Terms") govern your subscription to services provided by File Business INC. ("we," "us," or "our"). By subscribing to or using our subscription-based services ("Subscription Services"), you agree to be bound by these Terms, as well as our Terms of Service and Privacy Policy. If you do not agree to these Terms, you must not subscribe or use our services.`}
        />
      </div>
      <main className="px-5 xl:px-[270px] mx-auto w-full bg-primary-bg xl:py-12 py-16">
        <section className="mb-16">
          <LegalText
            title="AGREEMENT TO SUBSCRIPTION TERMS"
            description={`These Subscription Terms ("Terms") govern your use of subscription-based services provided by File Business INC. ("FB," "Company," "we," "us," or "our"). By subscribing to or using our Subscription Services, you agree to be bound by these Terms, as well as our Terms of Use and Privacy Policy. If you do not agree, you must not subscribe or use our services.`}
          />
        </section>
        <section className="mb-16">
          <LegalItemsList
            title="SUBSCRIPTION OVERVIEW"
            subtitle="FB offers recurring business compliance services, including but not limited to:"
            lists={[
              {
                items: [
                  {
                    description: "Annual report filings"
                  },
                  {
                    description: "Beneficial Ownership Information (BOI) reporting"
                  },
                  {
                    description: "Registered Agent services"
                  },
                  {
                    description: "Corporate compliance and entity management"
                  },
                  {
                    description: "Federal EIN (Employer Identification Number) filings"
                  },
                  {
                    description: "Corporate amendment filings"
                  },
                  {
                    description: "Dissolution and reinstatement services"
                  },
                  {
                    description: "Foreign qualification filings"
                  },
                  {
                    description: "Annual compliance monitoring and alerts"
                  },
                  {
                    description: "Additional services provided through third-party providers"
                  },
                ]
              }
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="PAYMENT TERMS"
            steps={[
              {
                title: "FB may collect the following personal data",
                bullets: [
                  "Subscriptions are billed at the start of each subscription period (e.g., monthly, annually) based on the selected plan.",
                  "By subscribing, you authorize FB to automatically charge your payment method on file at the start of each billing cycle.",
                ]
              },
              {
                title: "Automatic Renewal",
                bullets: [
                  "Your subscription automatically renews at the end of each billing cycle unless you cancel prior to the renewal date.",
                  "By agreeing to these Terms, you authorize FB to charge your payment method for each renewal period.",
                ]
              },
              {
                title: "Sufficient Funds & Failed Payments:",
                bullets: [
                  "You must ensure sufficient funds are available for your subscription payments.",
                  "If a payment attempt fails (e.g., due to insufficient funds, expired card), FB is not obligated to provide continued service.",
                  "If a payment is not received after three attempts, we may suspend or terminate your subscription."
                ]
              },
              {
                title: "No Obligation for Bank Fees:",
                bullets: [
                  "FB is not responsible for any bank fees, overdraft charges, or insufficient funds penalties.",
                  "If a payment fails, you are responsible for resolving the issue with your bank or card provider.",
                ]
              },
              {
                title: "Suspension & Reinstatement:",
                bullets: [
                  "If your payment method fails after three attempts, your subscription may be suspended.",
                  "Service will only be reinstated once all outstanding balances are paid."
                ]
              }
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="CANCELLATION POLICY"
            steps={[
              {
                title: "Cancel Anytime",
                bullets: [
                  "You may cancel your subscription at any time by:",
                  "Logging into your account.",
                  "Submitting a cancellation request through the Support Center.",
                  "Cancellations take effect at the end of the current billing cycle.",
                ]
              },
              {
                title: "No Refunds for Unused Period",
                bullets: [
                  "No refunds or credits will be issued for any unused subscription time, except as required by law.",
                ]
              },
              {
                title: "Abandoned Subscriptions",
                bullets: [
                  "If a payment fails and the account remains inactive for 30+ days, the subscription may be considered abandoned and terminated.",
                  "FB is not liable for compliance failures caused by abandoned subscriptions.",
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="REFUND POLICY"
            steps={[
              {
                title: "No Refunds for Subscription Fee",
                bullets: [
                  "Subscription fees are non-refundable.",
                  "You remain responsible for subscription fees for the entire billing period, even if you cancel before the end of the cycle.",
                ]
              },
              {
                title: "Exception for FB Errors:",
                bullets: [
                  "Refunds are only available if an error on FB's part results in a failure to provide promised services.",
                  "Refund requests must be submitted via the Support Center."
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="SUBSCRIPTION MODIFICATIONS"
            steps={[
              {
                title: "Upgrades/Downgrades",
                bullets: [
                  "You may modify your subscription (upgrade or downgrade) through your account settings.",
                  "Changes take effect at the next billing cycle.",
                  "No refunds or credits are issued for downgrades within an active billing cycle."
                ]
              },
              {
                title: "Additional Services:",
                bullets: [
                  "Any additional services purchased outside of your subscription will be billed separately and are subject to separate terms.",
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="SUBSCRIPTION SUSPENSION AND TERMINATION"
            steps={[
              {
                title: "Suspension for Non-Payment",
                bullets: [
                  "If a payment method is declined and we cannot process payment, your subscription will be suspended.",
                  "FB is not responsible for service disruptions or compliance failures caused by suspension.",
                ]
              },
              {
                title: "Termination by USFC:",
                bullets: [
                  "We reserve the right to terminate your subscription at our discretion, with or without cause.",
                  "If termination occurs due to non-payment, policy violations, or misuse, no refunds will be provided."
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="FREE TRIALS AND PROMOTIONAL OFFERS"
            steps={[
              {
                title: "Free Trials",
                bullets: [
                  "If you sign up for a free trial, your trial begins on the date of sign-up and will continue for the specified period.",
                  "You must provide a valid payment method, which will be automatically charged if you do not cancel before the trial ends.",
                ]
              },
              {
                title: "Promotional Offers:",
                bullets: [
                  "Discounts and promotions apply only during the promotional period.",
                  "After the promotional period ends, standard subscription fees apply."
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <Steps
            title="INSTALLMENT PAYMENTS"
            steps={[
              {
                title: "Installment Plans",
                bullets: [
                  "In some cases, FB may offer installment payment options for certain services.",
                  "If you choose an installment plan, you agree to timely payments.",
                ]
              },
              {
                title: "Missed Installments:",
                bullets: [
                  "If a scheduled installment payment is missed, FB may suspend services until the payment is made.",
                  "We reserve the right to charge the remaining balance in a lump sum."
                ]
              },
            ]}
          />
        </section>
        <section className="mb-16">
          <LegalItemsList
            title="GOVERNING LAW AND DISPUTE RESOLUTION"
            lists={[
              {
                items: [
                  {
                    description: "These Subscription Terms are governed by the laws of the State of Florida."
                  },
                  {
                    description: "Any disputes related to subscription services must be resolved through binding arbitration in Tallahassee, Florida."
                  },
                  {
                    description: "Class-action lawsuits are waived."
                  },

                ]
              }
            ]}
          />
        </section>
        <section className="mb-16">
          <LegalItemsList
            title="CHANGES TO SUBSCRIPTION TERMS"
            lists={[
              {
                items: [
                  {
                    description: "FB may revise these Subscription Terms at any time."
                  },
                  {
                    description: "If changes materially affect your rights, we will notify you via email or account notification."
                  },
                  {
                    description: "Continued use of the subscription after changes take effect constitutes acceptance of the revised Terms."
                  },

                ]
              }
            ]}
          />
        </section>
        <section>
          <ContactInfo title="CONTACT INFORMATION" pageName="Subscription Terms" />
        </section>
      </main>
    </div>
  );
}